import dayjs from "dayjs";
import { COUNTRIES_DATA } from "../constants/latlongData";
import { CONSENT_STATUS, CONSENT_STATUS_KEYS, CONSENT_STATUS_KEYS_MAP, TIME_RANGE, TIME_UNITS } from "../constants/constants";

// world map chart
export const getCountryCoordinates = (countryName: string) => {
  return countryName ? COUNTRIES_DATA?.filter((obj) => obj.country === countryName)?.[0] : null;
};

// age chart
type AgeData = { [key: string]: any };
type AgeRange = { min: number; max: number; label: string };
type RangeObject = { [key: string]: number };

export const createDynamicAgeRangeObject = (
  ageData: AgeData,
  customAgeRanges?: string[]
): RangeObject => {
  const ages = ageData && Object.keys(ageData).map((age) => parseInt(age, 10));
  const maxAge = ages && Math.max(...ages);

  // Parse the custom age ranges if provided
  const parseCustomAgeRanges = (ranges: string[]): AgeRange[] => {
    return ranges.map((range) => {
      const [start, end] = range.split("-").map((age) => parseInt(age, 10));
      return {
        min: start,
        max: end || maxAge, // Use maxAge for open-ended ranges like "35+"
        label: end ? range : `${start}+`,
      };
    });
  };

  // Default logic to generate dynamic age ranges if no custom ranges are provided
  const generateDefaultAgeRanges = (
    min: number,
    max: number,
    rangeSize: number
  ): AgeRange[] => {
    const ranges: AgeRange[] = [];
    for (let start = min; start <= max; start += rangeSize) {
      const end = Math.min(start + rangeSize - 1, max);
      ranges.push({
        min: start,
        max: end,
        label: start === end ? `${start}+` : `${start}-${end}`,
      });
    }
    return ranges;
  };

  const rangeSize = 7;
  const ageRanges = customAgeRanges
    ? parseCustomAgeRanges(customAgeRanges)
    : generateDefaultAgeRanges(0, maxAge, rangeSize);

  const rangeObject: RangeObject = {};

  if (ageData) {
    for (const [age, value] of Object.entries(ageData)) {
      const ageNum = parseInt(age, 10);

      const range = ageRanges.find((r) => ageNum >= r.min && ageNum <= r.max);
      if (range) {
        rangeObject[range.label] =
        (rangeObject[range.label] || 0) + parseInt(value, 10);
      }
    }
  }

  return rangeObject;
};

// donut chart
export const findKeyword = (str: string): string => {
  return CONSENT_STATUS_KEYS_MAP[str] || "";
};

interface StatusData {
  name: string;
  y: number;
}

export const checkAndAddStatus = (arr: StatusData[]): StatusData[] => {
  const presentStatuses = arr.map((obj) => obj.name);
  CONSENT_STATUS.forEach((status) => {
    if (!presentStatuses.some((name) => name.includes(status))) {
      arr.push({ name: status, y: 0 });
    }
  });
  return arr;
};

export const getColorFromStatus = (status: string) => {
  switch (status) {
    case "Pending":
      return "#9930DC";
    case "Rejected":
      return "#4F6483";
    case "Approved":
      return "#47B872";
    case "Self":
      return "#AEDAEA";
    case "Revoked Re.":
      return "#4f853f"
    case "Revoked Ap.":
      return "#FF9999";
    default:
      return '';
  }
};

interface ChartData {
  name: string;
  y: number;
}

interface ProcessedChartData {
  title: string;
  data: ChartData[];
  totalSum: number;
}

export const processData = (
  data: Record<string, Record<string, string>>
): ProcessedChartData[] => {
  return (
    data &&
    Object.keys(data).map((key) => {
      const values = data[key];
      const chartData: ChartData[] = Object.keys(values).map((subKey) => ({
        ...(findKeyword(subKey)
          ? { name: findKeyword(subKey) }
          : { name: subKey }),
        y: parseInt(values[subKey], 10),
        color: getColorFromStatus(findKeyword(subKey)),
      }));
      checkAndAddStatus(chartData); // Add Status As 0, If Any Status Missing
      const totalSum = Object.keys(values).reduce((sum, subKey) => {
        if (CONSENT_STATUS_KEYS.some(keyword => subKey.includes(keyword))) {
          return sum + parseInt(values[subKey], 10);
        }
        return sum;
      }, 0);
      return { title: key, data: chartData, totalSum };
    })
  );
};

// last 5 miutes consents chart
interface DataEntry {
  request_time: string; 
}

interface RangeCount {
  range: string;
  count: number;
}

export const get5MinutesGroupData = (data: DataEntry[]): RangeCount[] => {
  const currentTime = dayjs();

  data?.forEach((entry) => {
    const recivedTime = dayjs(entry.request_time);
    if (
      recivedTime > currentTime.subtract(5, "minute") &&
      currentTime > recivedTime
    ) {
      TIME_RANGE.now++;
    } else if (
      recivedTime > currentTime.subtract(10, "minute") &&
      currentTime > recivedTime
    ) {
      TIME_RANGE["10mins"]++;
    } else if (
      recivedTime > currentTime.subtract(15, "minute") &&
      currentTime > recivedTime
    ) {
      TIME_RANGE["15mins"]++;
    } else if (
      recivedTime > currentTime.subtract(20, "minute") &&
      currentTime > recivedTime
    ) {
      TIME_RANGE["20mins"]++;
    } else if (
      recivedTime > currentTime.subtract(25, "minute") &&
      currentTime > recivedTime
    ) {
      TIME_RANGE["25mins"]++;
    }
  });

  return Object.keys(TIME_RANGE).map((range) => ({
    range,
    count: TIME_RANGE[range],
  }));
};



export const getGroupedData = (
  data: DataEntry[],
  totalRange: number,
  interval: number,
  unit: dayjs.ManipulateType
): RangeCount[] => {
  const rangeCounts: RangeCount[] = [];
  const currentTime = dayjs();

  // Initialize range count array with the appropriate ranges
  for (let i = 0; i < totalRange; i += interval) {
    const rangeLabel = i === 0 ? "now" : `${i + interval}${TIME_UNITS[unit]}`;
    rangeCounts.push({
      range: rangeLabel,
      count: 0,
    });
  }

  data?.forEach((entry) => {
    const receivedTime = dayjs(entry.request_time);
    const timeDiff = currentTime.diff(receivedTime, unit);

    for (let i = 0; i < rangeCounts.length; i++) {
      const range = rangeCounts[i];
      const endRange = i === 0 ? interval : i * interval;

      if (timeDiff < endRange) {
        range.count++;
        break;
      }
    }
  });

  return rangeCounts;
};

